import {
  Button,
  Flex,
  Input,
  Box,
  Container,
  Image,
  useBreakpointValue,
  Text,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useFeatureFlagFromContext } from '../../launchdarkly';
import birdLogo from '../../media/images/Logo.png';
import bg from '../../media/images/background.png';
import LoginImage from '../../media/images/LoginImage.svg';
import MiniFormCard from '../utility/templates/miniFormCard';
import apiEndpoint from '../config/data';
import {
  Button as NexusButton,
  TextInput,
  HeadingText,
} from '@spotoninc/nexus-react';
import SpotOnIcon from '../../media/images/SpotOnIcon.svg';

const ResetPassword = () => {
  // State variables
  const [code, setCode] = useState(null);
  const [email, setEmail] = useState('');
  const [buttonLoader, setButtonLoader] = useState(false);

  // Create refs for password inputs
  const passwordRef = useRef(null);
  const password2Ref = useRef(null);

  // Get the showReskin feature flag
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // Responsive values for the reskinned version
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  // Track whether the API call has been made
  const apiCallMadeRef = useRef(false);

  // ComponentDidMount equivalent
  useEffect(() => {
    // Only run once
    if (apiCallMadeRef.current) return;

    window.scrollTo(0, 0);
    const queryParameters = new URLSearchParams(window.location.search);
    const codeParam = queryParameters.get('code');
    setCode(codeParam);

    if (codeParam === null) {
      alert('Invalid Link.');
      window.open('/', '_self');
    } else {
      // Mark that we're making the API call
      apiCallMadeRef.current = true;

      var formData = new FormData();
      formData.append('token', codeParam);

      // verify code
      fetch(apiEndpoint + '/api/verifyCode/', {
        method: 'POST',
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          if (data['verified'] === 'pass') {
            setEmail(data['companyEmail']);
          } else {
            alert('The link has expired.');
            window.open('/', '_self');
          }
        })
        .catch(err => {
          console.error(err);
          alert('The link has expired.');
        });
    }
  }, []);

  // Track whether the reset API call is in progress
  const resetApiInProgressRef = useRef(false);

  // Reset password function
  const resetCheck = () => {
    // Prevent multiple simultaneous API calls
    if (resetApiInProgressRef.current) return;

    setButtonLoader(true);
    resetApiInProgressRef.current = true;

    // Get password values using refs for the reskinned version or document.getElementById for the original
    const password =
      showReskin && passwordRef.current
        ? passwordRef.current.value
        : document.getElementById('password').value;

    const password2 =
      showReskin && password2Ref.current
        ? password2Ref.current.value
        : document.getElementById('password2').value;

    if (password === password2) {
      var formData = new FormData();
      formData.append('token', code);
      formData.append('password', password);
      formData.append('email', email);

      fetch(apiEndpoint + '/api/update_password/', {
        method: 'POST',
        body: formData,
      })
        .then(data => data.json())
        .then(data => {
          resetApiInProgressRef.current = false;
          setButtonLoader(false);
          alert(data['status']);
          window.open('/', '_self');
        })
        .catch(err => {
          console.error(err);
          resetApiInProgressRef.current = false;
          setButtonLoader(false);
        });
    } else {
      alert('Password Mismatch.');
      resetApiInProgressRef.current = false;
      setButtonLoader(false);
    }
  };

  if (showReskin) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="container.xxl" px={4}>
          <Flex
            minH="100vh"
            align="center"
            justify="center"
            direction={{ base: 'column', lg: 'row' }}
          >
            {/* Left side - Login Image */}
            {isDesktop && (
              <Box
                flex="1"
                maxW="800px"
                h="100vh"
                position="relative"
                overflow="hidden"
              >
                <Image
                  src={LoginImage}
                  alt="Login Image"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              </Box>
            )}

            {/* Right side - Reset Password Form */}
            <Box
              flex="1"
              maxW={{ base: '100%', lg: '800px' }}
              w="full"
              bg="#f7fafc"
              rounded="xl"
              boxShadow="xl"
              h="100vh"
              display="flex"
              alignItems="center"
              // justifyContent="center"
            >
              <Flex alignSelf="flex-start" ml={8} mt={8}>
                <Image src={SpotOnIcon} alt="SpotOn Logo" w="20px" mr={3} />
                <Text fontWeight={'600'} fontSize={'16px'}>
                  Profit Assist
                </Text>
              </Flex>
              <Flex
                direction="column"
                align="center"
                maxW="400px"
                w="100%"
                p={8}
                gap={6}
                justifyContent="center"
              >
                <HeadingText variant="heading2" color="neutral.900">
                  Reset Password
                </HeadingText>

                <Flex direction="column" width="100%" gap={4}>
                  <TextInput
                    type="password"
                    label="Enter New Password"
                    ref={passwordRef}
                    id="password"
                    style={{ width: '100%' }}
                  />

                  <TextInput
                    type="password"
                    label="Re-enter Password"
                    ref={password2Ref}
                    id="password2"
                    style={{ width: '100%' }}
                  />
                </Flex>

                <NexusButton
                  variant="primary"
                  onClick={resetCheck}
                  isLoading={buttonLoader}
                  fullWidth
                  className="centered-spinner-button"
                >
                  Reset
                </NexusButton>
              </Flex>
            </Box>
          </Flex>
        </Container>
      </Box>
    );
  } else {
    return (
      <>
        <MiniFormCard
          header="Forgot Password"
          headerDescription="Enter your email id to reset your password"
          logo={birdLogo}
          bg={bg}
          p={10}
        >
          <FormControl isRequired width={'100%'} mb={4}>
            <FormLabel fontSize={'12'} fontWeight={'bold'}>
              Enter New Password
            </FormLabel>
            <Input type="password" width={'100%'} id="password" />
          </FormControl>
          <FormLabel fontSize={'12'} fontWeight={'bold'}>
            Re enter Password
          </FormLabel>
          <Input type="password" width={'100%'} id="password2" />

          <Flex
            align={'center'}
            justify={'center'}
            width={'100%'}
            direction={'column'}
          >
            <Button
              width={'100%'}
              bg={'#faac35'}
              borderBottomRadius={'5'}
              borderTopRadius={'5'}
              textColor={'white'}
              mb={2}
              mt={4}
              isLoading={buttonLoader}
              onClick={resetCheck}
            >
              Reset
            </Button>
            {/*<Flex width={'100%'} direction={'column'} fontSize={'xs'}>
              <Text fontSize={'xs'} color={'gray.500'}>
                Not registered yet?{' '}
              </Text>
              <LnkNavigate color="blue" link="/register">
                Create an Account!
              </LnkNavigate>
            </Flex>*/}
          </Flex>
        </MiniFormCard>
      </>
    );
  }
};

export default ResetPassword;

//<Flex alignItems={'center'} width={'100%'} pl={'10%'}>
//<Fade>
//  <Card
//    align={'center'}
//    borderRadius={'10'}
//    boxShadow={'2xl'}
//    fontFamily={'DM Sans, sans-serif'}
//    p={'9vH'}
//    flex={1}
//    zIndex={200}
//  >
//    <CardBody justifyContent={'space-between'} width={400}>
//      <Flex
//        direction={'column'}
//        textAlign={'left'}
//        width={'100%'}
//        mb={4}
//        align={'center'}
//      >
//        <Image src={birdLogo} height={'auto'} width={100} />
//        <Flex textAlign={'left'} width={'100%'} direction={'column'}>
//          <Heading fontSize={'2xl'} mb={1}>
//            Forgot Password
//          </Heading>
//          <Text fontSize={10} color={'gray.500'}>
//            Enter your email to reset your password!
//          </Text>
//        </Flex>
//      </Flex>
//      <FormControl isRequired width={'100%'} mb={4}>
//        <FormLabel fontSize={'12'} fontWeight={'bold'}>
//          Email
//        </FormLabel>
//        <Input
//          type="email"
//          placeholder="mailId@domain.com"
//          width={'100%'}
//        />
//      </FormControl>
//
//      {/*<ReCAPTCHA render="explicit" sitekey="your_site_key" />*/}
//      <Flex
//        align={'center'}
//        justify={'center'}
//        width={'100%'}
//        direction={'column'}
//      >
//        <Button
//          width={'100%'}
//          bg={'#3311db'}
//          borderBottomRadius={'5'}
//          borderTopRadius={'5'}
//          textColor={'white'}
//          mb={2}
//        >
//          Reset
//        </Button>
//        <Flex width={'100%'} textAlign={'left'}>
//          <Text fontSize={'xs'} color={'gray.500'}>
//            Not registered yet?{' '}
//            <Link color="blue">Create an Account!</Link>
//          </Text>
//        </Flex>
//      </Flex>
//    </CardBody>
//  </Card>
//</Fade>
//
//<Image src={bg} flex={2}></Image>
//</Flex>
